// @ts-ignore
import mantineStyles from "@mantine/core/styles.css?url";
import mantineDateStyles from "@mantine/dates/styles.css?url";
import mantineDropzone from "@mantine/dropzone/styles.css?url";
import {
  captureRemixErrorBoundaryError,
  SentryMetaArgs,
  setUser,
  withSentry,
} from "@sentry/remix";
import * as Sentry from "@sentry/remix";
import { ClerkApp, SignedIn, UserButton, useUser } from "@clerk/remix";
import { rootAuthLoader } from "@clerk/remix/ssr.server";
// @ts-ignore
import rdgStyles from "@inovua/reactdatagrid-community/index.css?url";
// @ts-ignore
import phoneNumberInputStyles from "react-phone-number-input/style.css?url";
import {
  AppShell,
  Button,
  Group,
  LoadingOverlay,
  MantineProvider,
  NavLink,
  ScrollArea,
  Title,
} from "@mantine/core";
import {
  Link,
  Links,
  Meta,
  NavLink as RemixNavLinkImport,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import { getOrgId } from "~/helpers/getOrgId";
import React, { useEffect } from "react";
import {
  ErrorBoundary as AppErrorBoundary,
  FallbackProps,
} from "react-error-boundary";
import { CgTrack } from "react-icons/cg";
import { FaRegAddressCard, FaShippingFast } from "react-icons/fa";
import { FaPeopleCarryBox } from "react-icons/fa6";
import { MdBusiness, MdSupportAgent } from "react-icons/md";
import { ImPriceTags } from "react-icons/im";
import { MdSettings, MdPublic } from "react-icons/md";
import { GrAdd } from "react-icons/gr";
import { TbMailbox } from "react-icons/tb";
import { PiClockCountdown } from "react-icons/pi";
import { LuPackagePlus } from "react-icons/lu";
import prisma from "~/db.server";
// @ts-ignore
import globalStyles from "~/styles/global.css?url";
import { RemixNavLinkProps } from "@remix-run/react/dist/components";
import { LoaderFunctionArgs, MetaFunction, redirect } from "@remix-run/node";
import { User } from "@clerk/backend";
import { ModalsProvider } from "@mantine/modals";
import { getStaffRole } from "~/helpers/getStaffStatus";
import { BsReceipt } from "react-icons/bs";
import { StaffRole } from "@prisma/client";
import { SentryBugButton } from "./components/SentryBugButton";

export const links = () => {
  return [
    { rel: "stylesheet", href: globalStyles },
    { rel: "stylesheet", href: mantineStyles },
    { rel: "stylesheet", href: mantineDateStyles },
    { rel: "stylesheet", href: mantineDropzone },
    { rel: "stylesheet", href: rdgStyles },
    { rel: "stylesheet", href: phoneNumberInputStyles },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/apple-touch-icon.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "manifest", href: "/site.webmanifest" },
  ];
};

async function upsertUser(user: User) {
  let email,
    _email = user.emailAddresses.find(
      ({ id }) => user.primaryEmailAddressId === id,
    );
  if (!_email || _email.verification?.status !== "verified")
    throw new Error("Missing user email");
  else email = _email.emailAddress;
  if (!user.firstName || !user.lastName)
    throw new Error("Missing user name/surname");
  const userDto = {
    firstName: user.firstName,
    lastName: user.lastName,
    email,
  };
  const dbUser = await prisma.user.upsert({
    where: {
      id: user.id,
    },
    update: userDto,
    create: {
      id: user.id,
      ...userDto,
    },
  });
}

export const loader = (args: LoaderFunctionArgs) =>
  rootAuthLoader(
    args,
    async ({ request: { url, user } }) => {
      if (url.includes("/sign-in") || url.includes("/sign-up")) {
        if (user) return { redirect: "/" };
        return {};
      }
      if (!user) return redirect("/sign-in");
      await upsertUser(user);
      if (url.includes("/require-approval")) {
        if (!user.publicMetadata.approved) return {};
        try {
          await getOrgId(user?.id);
          return { redirect: "/" };
        } catch (error) {
          return {};
        }
      }
      let staffRole;
      try {
        staffRole = await getStaffRole(user?.id);
      } catch (e) {}
      if (!user?.publicMetadata.approved)
        return { redirect: "/require-approval" };
      try {
        await getOrgId(user?.id);
      } catch (error) {
        return { organisation: false, staffRole };
      }
      return { organisation: true, staffRole };
    },
    { loadUser: true },
  );

const RemixNavLink = (
  props: Omit<
    RemixNavLinkProps & React.RefAttributes<HTMLAnchorElement>,
    "className"
  >,
) => <RemixNavLinkImport {...props} className="link-button" />;

export const meta = ({ data }: SentryMetaArgs<MetaFunction<typeof loader>>) => {
  if (!data) return [];
  let value = [];
  if (data?.sentryTrace)
    value.push({
      name: "sentry-trace",
      content: data.sentryTrace,
    });
  if (data?.sentryBaggage)
    value.push({
      name: "baggage",
      content: data.sentryBaggage,
    });
  return value;
};

function App() {
  const navigate = useNavigate();
  const data = useLoaderData<{
    redirect?: string;
    organisation?: boolean;
    staffRole?: StaffRole;
  }>();
  const location = useLocation();
  const { state } = useNavigation();
  const user = useUser();
  // const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    if (!user.isLoaded || !user.isSignedIn) return;
    let _user: Record<string, string | number> = {};
    let sentryUser: { id: string; email?: string } = { id: user.user?.id };
    if (user.user?.fullName) _user.name = user.user?.fullName;
    if (user.user?.primaryEmailAddress?.emailAddress) {
      let email = user.user?.primaryEmailAddress?.emailAddress;
      _user.email = email;
      sentryUser.email = email;
    }

    setUser(sentryUser);
  }, [user]);

  useEffect(() => {
    if (data?.redirect && location.pathname !== data.redirect)
      navigate(data.redirect);
  }, [data.redirect]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        {/* <ColorSchemeScript /> */}
      </head>
      <body>
        <MantineProvider forceColorScheme="light">
          <ModalsProvider>
            <AppShell
              padding="md"
              navbar={{
                width: 200,
                breakpoint: "sm",
                collapsed: { desktop: false, mobile: false },
              }}
              header={{ height: 60 }}
              styles={(theme) => ({
                main: {
                  backgroundColor:
                    // colorScheme === "dark"
                    //   ? theme.colors.dark[8],
                    //   :
                    theme.colors.gray[0],
                },
              })}
            >
              <AppShell.Header>
                <Group h="60px" px={20} justify="space-between">
                  <Group justify="flex-start">
                    <img
                      src="/logo.png"
                      style={{ height: "45px" }}
                      alt="Prime International Express"
                    />
                  </Group>
                  <Group justify="flex-end">
                    <SentryBugButton />
                    {(location.pathname === "/drafts" ||
                      location.pathname === "/addresses" ||
                      location.pathname === "/admin/rate-groups" ||
                      location.pathname === "/admin/customers" ||
                      location.pathname === "/admin/agents") && (
                      <NavLink
                        component={Link}
                        label="Create"
                        leftSection={<GrAdd size={25} />}
                        className="link-button"
                        to={`${location.pathname}/new`}
                      />
                    )}
                    <UserButton showName signInUrl="/sign-in" />
                  </Group>
                </Group>
              </AppShell.Header>
              <AppShell.Navbar p="sm">
                <SignedIn>
                  <AppShell.Section mt="xs" mb={70} grow component={ScrollArea}>
                    <Title order={4}>Quick Actions</Title>
                    {/*{!!data.organisation && (
                      <RemixNavLink to="/quick-quote">
                        {({ isActive, isPending }) => (
                          <NavLink
                            component="button"
                            label="Quick Estimate"
                            description="Get an indicative estimate"
                            active={isActive}
                            leftSection={<PiClockCountdown size={25} />}
                            variant={isPending ? undefined : "filled"}
                          />
                        )}
                      </RemixNavLink>
                    )}*/}
                    {(!!data.organisation || !!data.staffRole) && (
                      <>
                        <RemixNavLink to="/drafts/new">
                          {({ isActive, isPending }) => (
                            <NavLink
                              component="button"
                              label="New Shipment"
                              description="Create new shipment"
                              active={isActive}
                              leftSection={<LuPackagePlus size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <Title order={4} mt="md">
                          Manage Shipments
                        </Title>
                        <RemixNavLink to="/drafts">
                          {({ isActive, isPending }) => (
                            <NavLink
                              component="button"
                              label="Saved For Later"
                              description="View Pending Shipments"
                              active={
                                isActive && location.pathname !== "/drafts/new"
                              }
                              leftSection={<TbMailbox size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/pickups">
                          {({ isActive, isPending }) => (
                            <NavLink
                              component="button"
                              label="Collections"
                              description="View scheduled collections"
                              active={isActive}
                              leftSection={<FaPeopleCarryBox size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/shipments">
                          {({ isActive, isPending }) => (
                            <NavLink
                              component="button"
                              label="Processed"
                              description="View processed shipments"
                              active={isActive}
                              leftSection={<FaShippingFast size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <Title order={4} mt="md">
                          Other Tools
                        </Title>
                        <RemixNavLink to="/addresses">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Address Book"
                              component="button"
                              description="Manage your saved addresses"
                              active={isActive}
                              leftSection={<FaRegAddressCard size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                      </>
                    )}
                    <RemixNavLink to="/tracker">
                      {({ isActive, isPending }) => (
                        <NavLink
                          component="button"
                          label="Tracking"
                          description="Provide a reference number to track"
                          active={isActive}
                          leftSection={<CgTrack size={25} />}
                          variant={isPending ? undefined : "filled"}
                        />
                      )}
                    </RemixNavLink>
                    {/*<RemixNavLink to="/pickups">*/}
                    {/*  {({ isActive, isPending }) => (*/}
                    {/*    <NavLink*/}
                    {/*      label="Pickups"*/}
                    {/* component="button" */}
                    {/*      description="View upcoming pickups"*/}
                    {/*      active={isActive}*/}
                    {/*      leftSection={<TbTruckDelivery size={25} />}*/}
                    {/*      variant={isPending ? undefined : "filled"}*/}
                    {/*    />*/}
                    {/*  )}*/}
                    {/*</RemixNavLink>*/}
                    {/*<RemixNavLink to="/dropoffs">*/}
                    {/*  {({ isActive, isPending }) => (*/}
                    {/*    <NavLink*/}
                    {/*      label="Dropoffs"*/}
                    {/* component="button" */}
                    {/*      description="View upcoming dropoffs"*/}
                    {/*      active={isActive}*/}
                    {/*      leftSection={<GiHandTruck size={25} />}*/}
                    {/*      variant={isPending ? undefined : "filled"}*/}
                    {/*    />*/}
                    {/*  )}*/}
                    {/*</RemixNavLink>*/}
                    {!!data.staffRole && (
                      <>
                        <Title order={4} mt="md">
                          Admin
                        </Title>
                        <RemixNavLink to="/admin/customer-quote">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Customer Quote"
                              component="button"
                              active={isActive}
                              leftSection={<BsReceipt size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/admin/customers">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Customers"
                              component="button"
                              active={isActive}
                              leftSection={<MdBusiness size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/admin/agents">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Agents"
                              active={isActive}
                              component="button"
                              leftSection={<MdSupportAgent size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/admin/rate-groups">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Rate Cards"
                              component="button"
                              active={isActive}
                              leftSection={<ImPriceTags size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/admin/configuration">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Configuration"
                              component="button"
                              active={isActive}
                              leftSection={<MdSettings size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                        <RemixNavLink to="/admin/countries">
                          {({ isActive, isPending }) => (
                            <NavLink
                              label="Countries"
                              component="button"
                              active={isActive}
                              leftSection={<MdPublic size={25} />}
                              variant={isPending ? undefined : "filled"}
                            />
                          )}
                        </RemixNavLink>
                      </>
                    )}
                  </AppShell.Section>
                </SignedIn>
              </AppShell.Navbar>
              <AppShell.Main>
                <AppErrorBoundary
                  fallbackRender={fallbackRender}
                  onReset={(details) => {
                    console.log(details);
                    window.location.reload();
                  }}
                >
                  <>
                    <LoadingOverlay
                      visible={state !== "idle"}
                      overlayProps={{ radius: "sm", blur: 2 }}
                    />
                    <Outlet />
                  </>
                </AppErrorBoundary>
              </AppShell.Main>
            </AppShell>
            <ScrollRestoration />
            <Scripts />
          </ModalsProvider>
        </MantineProvider>
      </body>
    </html>
  );
}

export default withSentry(ClerkApp(App));

export function ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  useEffect(() => {
    setTimeout(() => {
      const eventId = Sentry.lastEventId();
      Sentry.showReportDialog({ eventId });
    }, 250);
  }, []);

  let content = null;

  if (isRouteErrorResponse(error)) {
    content = (
      <div>
        <h1>
          {error.status}: {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  } else if (error instanceof Error) {
    content = (
      <div>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }

  console.error("error", error);
  return (
    <html>
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <MantineProvider forceColorScheme="light" defaultColorScheme="light">
          <h1>Whoops!</h1>

          {content}
          <Button onClick={() => location.reload()}>Retry</Button>
          <Scripts />
        </MantineProvider>
      </body>
    </html>
  );
}

function fallbackRender({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre style={{ color: "red" }}>{error.message}</pre>
      <Button onClick={resetErrorBoundary}>Retry</Button>
    </div>
  );
}

// export const ErrorBoundary = ({
//   error,
// }: {
//   location: Location;
//   component: ErrorBoundaryComponent;
//   error?: Error;
// }) => {
//   if (!error) return null;
//   return (
//     <Rekindled traceEndpoint="/error" error={error}>
//       <h1>Whoops!</h1>
//       <h2>We ran into an error: {error.message}</h2>
//       <p>{error.stack}</p>
//     </Rekindled>
//   );
// };
